import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mdiAccountOutline, mdiHelpCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { Capacitor } from "@capacitor/core";
import Banner from "components/Banner";
import PullToRefreshIndicator from "components/PullToRefreshIndicator";
import { routes } from "constants/routes";
import useDeviceType from "hooks/useDeviceType";
import useNetworkStatus from "hooks/useNetworkStatus";
import { RootState } from "store";
import { useModal } from "context/ModalContext";
import MobileNavigationBar from "./MobileNavigationBar";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface IProps {
  desktopTitle?: string;
  title?: string;
  subtitle?: string;
  customHeader?: React.ReactNode;
  hideSubHeader?: boolean;
  children: React.ReactNode;
  className?: string;
  containerClass?: string;
  hideNavigation?: boolean;
}

export default function MainLayout({
  desktopTitle,
  title,
  subtitle,
  customHeader,
  hideSubHeader = false,
  children,
  className,
  containerClass,
  hideNavigation = false
}: Readonly<IProps>) {
  const navigate = useNavigate();
  const { isOnline } = useNetworkStatus();
  const { isDesktop } = useDeviceType();
  const { openModal } = useModal();

  const { account, userToken } = useSelector((state: RootState) => state.authState);
  const { userDetails } = useSelector((state: RootState) => state.userState);
  const userType = account?.type;
  const businessName = userDetails?.dba;
  const isNewFrontendEnabled = userDetails?.frontend_settings?.enable_new_frontend;
  const redirectToNewFrontend = userDetails?.frontend_settings?.redirect_to_new_frontend;
  const isDomainSwitched =
    process.env.REACT_APP_LEGACY_HAULPAY_APP_URL ?? userDetails?.frontend_settings?.domain_switched;
  const { trackAllEvents } = useAnalytics();
  const isNative = Capacitor.isNativePlatform();
  const showBanner = isNewFrontendEnabled && !isNative;
  const mainContentRef = React.useRef<HTMLDivElement>(null);

  const goToUserSettings = () => {
    navigate(routes.SETTINGS);
  };

  const goToHelp = () => {
    navigate(routes.SETTINGS_HELP);
  };

  return (
    <>
      <div
        ref={mainContentRef}
        className={clsx(styles.mainContent, containerClass, title === "Home" && "!bg-accent")}>
        <PullToRefreshIndicator scrollRef={mainContentRef} />
        {showBanner && (
          <Banner
            centerText
            className={clsx(styles.safeBanner)}
            message={
              isDomainSwitched ? (
                <p>
                  Need the legacy app?{" "}
                  <a
                    href={`${process.env.REACT_APP_LEGACY_HAULPAY_APP_URL}/login?as=${userToken}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackAllEvents("banner_goto_legacyapp")}
                    className="underline btn-link inlineviewlink text-blue-950">
                    Click here
                  </a>
                  .
                </p>
              ) : (
                <p>
                  Use the old app{" "}
                  <a
                    href={`${process.env.REACT_APP_OLD_HAULPAY_APP_URL}/login?as=${userToken}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackAllEvents("banner_goto_oldapp")}
                    className="underline btn-link inlineviewlink text-blue-950">
                    here
                  </a>
                  . Please note that we will be retiring the old app in a few weeks.
                </p>
              )
            }
          />
        )}
        {/* Redirect to old app if new frontend is not enabled */}
        {isNewFrontendEnabled === false && !redirectToNewFrontend && (
          <RedirectToOldApp userToken={userToken} isNewFrontendEnabled={isNewFrontendEnabled} />
        )}
        <div
          className={clsx(styles.header, {
            [styles.headerWithSafeArea]: !showBanner,
            "bg-gradient-to-r mobile:from-card mobile:to-foreground": title === "Home"
          })}>
          <div className={styles.desktopHeader}>
            <h3 className={styles.desktopTitle}>{desktopTitle}</h3>
            <div className="flex flex-col items-end justify-end">
              {desktopTitle === "Settings" ? (
                <button
                  className={clsx(isOnline ? "" : "border border-error rounded-full !w-fit")}
                  onClick={goToHelp}>
                  <Icon path={mdiHelpCircleOutline} className="flex-none size-6" />
                </button>
              ) : (
                <button
                  className={clsx(styles.accountCard, isOnline ? "" : "border border-error")}
                  onClick={goToUserSettings}>
                  <div className={styles.avatarWrapper}>
                    <Icon path={mdiAccountOutline} className="m-auto size-6" />
                  </div>
                  <div className={styles.contactName}>
                    <span className="whitespace-pre">{businessName}</span>
                    <span className="text-xs text-placeholder">{userType}</span>
                  </div>
                </button>
              )}
              {!isOnline ? (
                <span className="font-bold text-error">Alert! You are currently offline</span>
              ) : null}
            </div>
          </div>

          {customHeader ??
            (!hideSubHeader ? (
              <div className={clsx(styles.subHeader, "desktop:!hidden")}>
                <div className="flex flex-col">
                  <h3 className={styles.logoTitle}>{title ?? "Finance"}</h3>
                  <span className={styles.subtitle}>{subtitle}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className={styles.accountButton}
                    onClick={() => openModal("crash-report", { source: "manual" })}
                    aria-label="Report a problem">
                    <Icon path={mdiHelpCircleOutline} />
                  </button>
                  <button
                    className={clsx(
                      styles.accountButton,
                      isOnline ? "" : "border border-error rounded-full"
                    )}
                    onClick={goToUserSettings}>
                    <Icon path={mdiAccountOutline} />
                  </button>
                </div>
              </div>
            ) : null)}
        </div>

        <div className={clsx(styles.mainWrapper, className)}>{children}</div>
      </div>

      {!hideNavigation && !isDesktop && <MobileNavigationBar />}
    </>
  );
}

function RedirectToOldApp({
  userToken,
  isNewFrontendEnabled
}: {
  userToken: string;
  isNewFrontendEnabled: boolean;
}) {
  React.useEffect(() => {
    window.location.replace(`${process.env.REACT_APP_OLD_HAULPAY_APP_URL}/login?as=${userToken}`);
  }, [userToken, isNewFrontendEnabled]);
  return null;
}
